import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useLanguage from '@clearscore/hooks.use-language';
import useMarketConfig from '@clearscore/hooks.use-market-config';
import FormOtp from '@shared/form-otp';
import Text from '@clearscore/rainbow.text';
import Icon from '@clearscore/rainbow.icons.padlock';

import { actions, selectors } from '../../redux';
import lang from './lib/lang';
import marketConfig from './lib/market-config';
import styles from './screen-verify.css';

const ScreenVerify = ({ location: { state = {} } }) => {
    const { requestMeta, coolDownSeconds, ...restState } = state;
    const hasLocationState = !!Object.keys(state).length;
    const language = useLanguage(lang);
    const { linkCantGetCode } = useMarketConfig(marketConfig);
    const dispatch = useDispatch();
    const resendStatus = useSelector(selectors.resendTwoFactorAuth.getStatus);
    const resendState = useSelector(selectors.resendTwoFactorAuth.getState);
    const verifyStatus = useSelector(selectors.verifyTwoFactorAuth.getStatus);
    const verifyState = useSelector(selectors.verifyTwoFactorAuth.getState);
    const handleResend = () => dispatch(actions.resendTwoFactorAuth(restState, requestMeta));
    const handleVerify = (code) => dispatch(actions.verifyTwoFactorAuth({ ...restState, code }, requestMeta));

    return (
        <Choose>
            <When condition={hasLocationState}>
                <div className={styles.title}>
                    <Text {...Text.presets.H1}>{language.title}</Text>
                </div>

                <div className={styles.description}>
                    <Text {...Text.presets.BODY1} dataId="login-screen-verify-description">
                        {language.description.replace('%mobileNumber%', state.mobileNumber)}
                    </Text>
                </div>

                <FormOtp
                    verifyOtp={handleVerify}
                    verifyOtpStatus={verifyStatus}
                    generateOtp={handleResend}
                    generateOtpStatus={resendStatus}
                    otpCoolDownSeconds={
                        coolDownSeconds || (resendState.data && resendState.data.cool_down_seconds) || 0
                    }
                    isResendLastAttempt={!!verifyState.data && verifyState.data.attempts_remaining === 1}
                />

                <div className={styles.footNotes}>
                    <Text {...Text.presets.LINK} href={linkCantGetCode} dataId="login-screen-verify-help-link">
                        {language.cantGetCode}
                    </Text>

                    <div className={styles.security}>
                        <Icon height={12} width={12} />
                        <Text {...Text.presets.BODY2}>{language.secureAndAuthorised}</Text>
                    </div>
                </div>
            </When>

            <Otherwise>
                <Redirect to="/login" />
            </Otherwise>
        </Choose>
    );
};

ScreenVerify.propTypes = {
    location: PropTypes.shape({
        state: PropTypes.shape({
            requestMeta: PropTypes.object.isRequired,
            mobileNumber: PropTypes.string.isRequired,
            coolDownSeconds: PropTypes.number.isRequired,
        }),
    }).isRequired,
};

export default ScreenVerify;
